/**
 * PromotionBurst — celebrates a sentence climbing the mastery ladder.
 * Fires once per promotion event (read from store), shows a ribbon for ~2s
 * with a special chime + GSAP entrance. Mounted globally in GameHud so any
 * study page can trigger it without prop drilling.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from '@/lib/gsap';
import { sfx } from '@/lib/audio';
import { TIERS } from './MasteryBadge';
import { useAppStore } from '@/lib/store';

export function PromotionBurst(): JSX.Element {
  const promo = useAppStore((s) => s.lastPromotion);
  const [visible, setVisible] = useState<{ from: number; to: number; seq: number } | null>(null);

  useEffect(() => {
    if (promo === null) return;
    setVisible({ from: promo.from, to: promo.to, seq: promo.seq });
    sfx.comboUp();
    const t = setTimeout(() => setVisible(null), 2200);
    return () => clearTimeout(t);
  }, [promo?.seq]);

  return (
    // On mobile sit just under the top nav strip (which is taller due to the
    // 2nd-row tab bar). On desktop drop further down so the ribbon is centered.
    <div className="pointer-events-none fixed inset-x-0 top-28 md:top-24 z-[58] grid place-items-center px-3">
      {visible !== null && <Ribbon key={visible.seq} from={visible.from} to={visible.to} />}
    </div>
  );
}

function Ribbon({ from, to }: { from: number; to: number }): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const tierBefore = TIERS[Math.min(3, Math.max(0, from)) as 0 | 1 | 2 | 3];
  const tierAfter = TIERS[Math.min(3, Math.max(0, to)) as 0 | 1 | 2 | 3];

  useLayoutEffect(() => {
    if (ref.current === null) return;
    const tl = gsap.timeline();
    tl.fromTo(ref.current,
      { opacity: 0, scale: 0.6, y: 30, filter: 'blur(10px)' },
      { opacity: 1, scale: 1, y: 0, filter: 'blur(0px)', duration: 0.55, ease: 'back.out(2.4)' },
    );
    tl.to(ref.current, {
      keyframes: [
        { x: -3, duration: 0.04 },
        { x: 3, duration: 0.04 },
        { x: 0, duration: 0.06 },
      ],
    }, 0.55);
    tl.to(ref.current, { opacity: 0, y: -16, duration: 0.4, ease: 'power2.in' }, 1.65);
    return () => { tl.kill(); };
  }, [from, to]);

  return (
    <div
      ref={ref}
      className="px-5 py-3 rounded-full bg-gradient-to-r from-honey-500/30 via-mint-500/40 to-ember-500/30 border border-mint-400/60 backdrop-blur-md flex items-center gap-3 glow-lime"
      style={{ opacity: 0 }}
    >
      <span className="font-arcade text-xs tracking-widest text-mint-300">PROMOTED</span>
      <span className="text-xs text-cream-300 line-through opacity-60 font-arcade">L{from} · {tierBefore.zh}</span>
      <span className="text-mint-300">→</span>
      <span className="font-arcade text-sm text-white">L{to} · {tierAfter.zh}</span>
      <span className="text-lg">✨</span>
    </div>
  );
}
